import type { ReactNode } from 'react';
import type { OrderStatus, OrderType, PaymentStatus, Role } from '../types';

export function StatusBadge({ status }: { status: OrderStatus }) {
  return status === 'COMPLETED' ? (
    <span className="badge badge-green">Completed</span>
  ) : (
    <span className="badge badge-red">Cancelled</span>
  );
}

export function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  return status === 'UNPAID' ? (
    <span className="badge badge-amber">Not paid</span>
  ) : (
    <span className="badge badge-green">Paid</span>
  );
}

export function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="badge badge-green">Active</span>
  ) : (
    <span className="badge badge-gray">Inactive</span>
  );
}

export function AvailableBadge({ available }: { available: boolean }) {
  return available ? (
    <span className="badge badge-green">Available</span>
  ) : (
    <span className="badge badge-amber">Unavailable</span>
  );
}

export function OrderTypeBadge({ type }: { type: OrderType }) {
  const map: Record<OrderType, string> = {
    DINE_IN: 'badge-blue',
    TAKEAWAY: 'badge-amber',
    DELIVERY: 'badge-gray',
  };
  const label: Record<OrderType, string> = {
    DINE_IN: 'Dine-in',
    TAKEAWAY: 'Takeaway',
    DELIVERY: 'Delivery',
  };
  return <span className={`badge ${map[type]}`}>{label[type]}</span>;
}

export function RoleBadge({ role }: { role: Role }) {
  const map: Record<Role, string> = {
    SUPER_ADMIN: 'badge-red',
    STORE_ADMIN: 'badge-blue',
    CASHIER: 'badge-gray',
  };
  const label: Record<Role, string> = {
    SUPER_ADMIN: 'Super Admin',
    STORE_ADMIN: 'Store Admin',
    CASHIER: 'Cashier',
  };
  return <span className={`badge ${map[role]}`}>{label[role]}</span>;
}

export function PageHead({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h2>{title}</h2>
        {subtitle && <div className="subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="row gap-sm flex-wrap">{actions}</div>}
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`card${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <div className="card-header">
          {title ? <h3>{title}</h3> : <span />}
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function Kpi({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: boolean;
}) {
  return (
    <div className={`kpi${accent ? ' kpi-accent' : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub != null && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}
